import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Recycle, Calculator, History, Settings, LogOut, TrendingUp, FileText, Users, ArrowRight, ChevronRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { calculationsAPI, adminAPI } from '../api/client';
import NotificationBell from '../components/NotificationBell';

const Dashboard = () => {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [calculations, setCalculations] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const calcsResponse = await calculationsAPI.getHistory();
      setCalculations(calcsResponse.data.slice(0, 5));
      if (isAdmin()) {
        const statsResponse = await adminAPI.getStats();
        setStats(statsResponse.data);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => new Date(dateString).toLocaleDateString('lt-LT', {
    year: 'numeric', month: 'short', day: 'numeric'
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navbar */}
      <header className="sticky top-0 z-50 w-full border-b border-gray-100 bg-white/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Recycle className="w-8 h-8 text-[#00C300]" />
            <span className="text-lg font-bold text-gray-900">taršosmokestis.lt</span>
          </Link>
          <div className="flex items-center gap-4">
            <NotificationBell />
            <div className="text-right hidden sm:block">
              <p className="text-sm font-semibold text-gray-900">{user?.company_name || user?.email}</p>
              <p className="text-xs text-gray-500">{user?.email}</p>
            </div>
            <Button variant="ghost" size="sm" onClick={logout} className="text-gray-600 hover:text-red-500 rounded-full">
              <LogOut className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Atsijungti</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-10">
        {/* Welcome */}
        <div className="mb-10">
          <h1 className="text-4xl font-extrabold text-gray-900 mb-2">
            Sveiki, {user?.company_name || user?.email}! 👋
          </h1>
          <p className="text-gray-500 text-lg">Valdykite savo taršos mokesčių apskaitas ir stebėkite istoriją</p>
        </div>

        {/* Admin Stats */}
        {isAdmin() && stats && (
          <div className="grid md:grid-cols-3 gap-6 mb-10">
            {[
              { icon: Users, color: 'text-blue-600 bg-blue-50', label: 'Viso vartotojų', value: stats.total_users },
              { icon: TrendingUp, color: 'text-[#00C300] bg-green-50', label: 'Organizacijos', value: stats.total_organizations },
              { icon: FileText, color: 'text-purple-600 bg-purple-50', label: 'Skaičiavimų', value: stats.total_calculations },
            ].map(({ icon: Icon, color, label, value }) => (
              <Card key={label} className="bg-white rounded-2xl border border-gray-100 shadow-sm">
                <CardContent className="p-6 flex items-center gap-4">
                  <div className={`w-14 h-14 ${color} rounded-2xl flex items-center justify-center`}>
                    <Icon className="w-7 h-7" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">{label}</p>
                    <p className="text-3xl font-extrabold text-gray-900">{value}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Quick Actions */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          {[
            { icon: Calculator, color: 'bg-green-100 text-[#00C300]', title: 'Naujas skaičiavimas', desc: 'Apskaičiuokite taršos mokestį visiems atliekų srautams', to: '/calculator' },
            { icon: History, color: 'bg-blue-100 text-blue-600', title: 'Skaičiavimų istorija', desc: 'Peržiūrėkite ankstesnius skaičiavimus ir ataskaitas', to: '/history' },
            ...(isAdmin() ? [{ icon: Settings, color: 'bg-purple-100 text-purple-600', title: 'Admin panelis', desc: 'Valdykite organizacijas ir sistemos nustatymus', to: '/admin' }] : []),
          ].map(({ icon: Icon, color, title, desc, to }) => (
            <Card key={title} className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300 cursor-pointer group"
              onClick={() => navigate(to)}>
              <CardHeader className="p-6">
                <div className={`w-14 h-14 ${color} rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <Icon className="w-7 h-7" />
                </div>
                <CardTitle className="text-xl font-bold text-gray-900 mb-2">{title}</CardTitle>
                <p className="text-gray-500 text-sm leading-relaxed">{desc}</p>
              </CardHeader>
              <div className="px-6 pb-6">
                <span className="text-[#00C300] text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                  Atidaryti <ChevronRight className="w-4 h-4" />
                </span>
              </div>
            </Card>
          ))}
        </div>

        {/* Recent Calculations */}
        <Card className="bg-white rounded-2xl border border-gray-100 shadow-sm">
          <CardHeader className="px-6 pt-6 pb-4 flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <History className="w-5 h-5 text-[#00C300]" />
                Paskutiniai skaičiavimai
              </CardTitle>
              <p className="text-gray-500 text-sm mt-1">Jūsų naujausios taršos mokesčio ataskaitos</p>
            </div>
            <Link to="/history">
              <Button variant="outline" size="sm" className="rounded-full border-2 border-gray-200 hover:border-[#00C300] hover:text-[#00C300] font-semibold">
                Visa istorija <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent className="px-6 pb-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-3 border-[#00C300] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : calculations.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Calculator className="w-8 h-8 text-gray-400" />
                </div>
                <p className="text-gray-500 mb-4 font-medium">Dar neturite jokių skaičiavimų</p>
                <Button onClick={() => navigate('/calculator')} className="bg-[#00C300] hover:bg-[#00A300] text-white rounded-full px-8 font-bold">
                  Pradėti skaičiavimą
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {calculations.map((calc) => (
                  <div key={calc.id} className="flex items-center justify-between p-4 rounded-2xl border border-gray-100 hover:border-[#00C300] hover:bg-green-50 transition-all">
                    <div>
                      <p className="text-xs text-gray-400 mb-1">{formatDate(calc.created_at)}</p>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-gray-600">Bazinis: <strong className="text-gray-900">€{calc.total_base_tax.toFixed(2)}</strong></span>
                        {calc.total_discount > 0 && (
                          <span className="text-[#00C300] font-semibold">-€{calc.total_discount.toFixed(2)}</span>
                        )}
                        <span className="text-gray-900 font-bold text-base">€{calc.total_final_tax.toFixed(2)}</span>
                      </div>
                    </div>
                    <Link to={`/calculation/${calc.id}`}>
                      <Button variant="outline" size="sm" className="rounded-full border-2 border-gray-200 hover:border-[#00C300] hover:text-[#00C300] font-semibold text-xs">
                        Peržiūrėti
                      </Button>
                    </Link>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
